import React from 'react';

import { useReducedMotion } from '@/nova/hooks/useReducedMotion';
import { ART_STYLE } from '../../theme/tokens';

/**
 * Reveal `units` progressively (0 → units) over at most `ART_STYLE.TYPE_REVEAL_MS`, whenever `trigger` changes
 * to a new non-zero value: the moment a fresh extraction lands, never on mount. Returns the units to show, or
 * `null` while nothing is being revealed (show everything). Under reduced motion it never animates.
 */
export const useTypedReveal = (units: number, trigger: number): number | null => {
  const reduced = useReducedMotion();
  const [shown, setShown] = React.useState<number | null>(null);
  // The length is read when the reveal STARTS: an edit mid-reveal must not restart it.
  const unitsRef = React.useRef(units);
  unitsRef.current = units;

  React.useEffect(() => {
    const total = unitsRef.current;
    if (trigger === 0 || reduced || total === 0) {
      setShown(null);
      return undefined;
    }
    let frame = 0;
    const start = performance.now();
    const tick = (now: number): void => {
      const progress = Math.min(1, (now - start) / ART_STYLE.TYPE_REVEAL_MS);
      // Ease out: brisk at first, settling as the last words land.
      const eased = 1 - (1 - progress) * (1 - progress);
      if (progress >= 1) {
        setShown(null);
        return;
      }
      setShown(Math.max(1, Math.round(total * eased)));
      frame = window.requestAnimationFrame(tick);
    };
    setShown(0);
    frame = window.requestAnimationFrame(tick);
    return () => window.cancelAnimationFrame(frame);
  }, [trigger, reduced]);

  return shown;
};

export default useTypedReveal;
